import { Box } from "@mui/material";
import React, { useContext } from "react";
import { createOptionsFromEnum } from "../../../../utils/formatters";
import {
  typeValueOptions,
  cortesyOptions,
  NFMMOptions,
  typeTax,
  DefaultTax,
  TaxData,
  ServiceValues,
  TaxKey,
  mapTypeTaxToTaxKey,
  issTypeValueOptions,
} from "../../../../types/budget/service-item";
import NumberFormat from "react-number-format";
import { BiChevronDown } from "react-icons/bi";
import {
  CategoryTotals,
  editBitributedRoles,
  ExchangeData,
} from "../../../../types/budget/budget";
import { AuthContext } from "../../../../contexts/auth";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import TaxWithConvertedValue from "../TaxWithConvertedValue";
import { useServices } from "../../../../contexts/ServicesContext/projectservices.context";

const valueTypeOptionsDefault = createOptionsFromEnum(typeValueOptions);
const percentageOnlyOptions = valueTypeOptionsDefault.filter(
  (option: any) => option.value === "PERCENTAGE"
);
const valueIssTypeOptionsDefault = createOptionsFromEnum(issTypeValueOptions);
const valueCortesyOptions = createOptionsFromEnum(cortesyOptions);
const typeTaxOptions = createOptionsFromEnum(typeTax);
const nfMMOptions = createOptionsFromEnum(NFMMOptions);

type Props = {
  taxData: TaxData | undefined;
  compareTaxData?: TaxData;
  exchange: ExchangeData;
  compareExchange: ExchangeData;
  itemType: string;
  onChange: (value: any, field: keyof ServiceValues) => void;
  totals: CategoryTotals;
};

function chunkArray(arr: any[], size: number) {
  return arr.reduce((acc: any[], _, i) => {
    if (i % size === 0) acc.push(arr.slice(i, i + size));
    return acc;
  }, []);
}

const BaseMonetaryData: React.FC<Props> = ({
  taxData,
  compareTaxData,
  exchange,
  itemType,
  onChange,
  compareExchange,
  totals,
}: Props) => {
  //   const { isBudgetDisabled } = useBudget();
  const { userRoleName } = useContext(AuthContext);
  const canEditBiTributed = editBitributedRoles.includes(userRoleName);
  const handleCalcValues = (
    type: string,
    taxParameters: { value: number; typeTax: string }
  ) => {
    const currState = JSON.parse(JSON.stringify(taxData));
    switch (type) {
      case "SERVICE":
        currState.serviceTax.value = taxParameters?.value || 0;
        currState.serviceTax.typeTax = taxParameters?.typeTax || "";
        break;
      case "ISS":
        currState.iss.value = taxParameters?.value || 0;
        currState.iss.typeTax = taxParameters?.typeTax || "";
        break;
      case "TARIFF":
        currState.tariff.value = taxParameters?.value || 0;
        currState.tariff.typeTax = taxParameters?.typeTax || "";
        break;
      case "LOCAL_TAX":
        currState.localTax.value = taxParameters?.value || 0;
        currState.localTax.typeTax = taxParameters?.typeTax || "";
        break;
      case "TOURISM_TAX":
        currState.tourismTax.value = taxParameters?.value || 0;
        currState.tourismTax.typeTax = taxParameters?.typeTax || "";
        break;
      case "FEE":
        currState.fee.value = taxParameters?.value || 0;
        currState.fee.typeTax = taxParameters?.typeTax || "";
        break;
      // case "NFMM":
      //   currState.typeNfMm.typeCalculate = taxParameters?.typeTax || "";
      //   break;
      case "IOF":
        currState.iof.value = taxParameters?.value || 0;
        currState.iof.typeTax = taxParameters?.typeTax || "";
        break;
      case "MARKUP":
        currState.markup.value = taxParameters?.value || 0;
        currState.markup.typeTax = taxParameters?.typeTax || "";
        break;
      case "OVER":
        currState.overhead.value = taxParameters?.value || 0;
        currState.overhead.typeTax = taxParameters?.typeTax || "";
        break;
      case "COMMISSION":
        currState.commission.value = taxParameters?.value || 0;
        currState.commission.typeTax = taxParameters?.typeTax || "";
        break;
      case "INCENTIVE":
        currState.incentive.value = taxParameters?.value || 0;
        currState.incentive.typeTax = taxParameters?.typeTax || "";
        break;
      case "TRIBUTE":
        currState.tribute.value = taxParameters?.value || 0;
        currState.tribute.typeTax = taxParameters?.typeTax || "";
        break;
      case "SUPPLIER_TRIBUTE":
        currState.supplier_tribute = {
          ...(currState.supplier_tribute || {
            currencyValue: 0,
            typeTax: "PERCENTAGE",
            value: 0,
            valueBRL: 0,
          }),
          value: taxParameters?.value || 0,
          typeTax: taxParameters?.typeTax || "PERCENTAGE",
        };
        break;
      case "DOUBLE_CHARGED":
        currState.doubleCharged.value = taxParameters?.value || 0;
        currState.doubleCharged.typeTax = taxParameters?.typeTax || "";
        break;
      default:
        break;
    }

    onChange(currState, "taxData");
  };
  function definePreviousValue(
    type: keyof typeof typeTax,
    referenceData: TaxData | undefined
  ) {
    switch (type) {
      case "SERVICE":
        return referenceData?.serviceTax;
      case "ISS":
        return referenceData?.iss;
      case "TARIFF":
        return referenceData?.tariff;
      case "LOCAL_TAX":
        return referenceData?.localTax;
      case "TOURISM_TAX":
        return referenceData?.tourismTax;
      case "FEE":
        return referenceData?.fee;
      case "IOF":
        return referenceData?.iof;
      case "MARKUP":
        return referenceData?.markup;
      case "OVER":
        return referenceData?.overhead;
      case "COMMISSION":
        return referenceData?.commission;
      case "INCENTIVE":
        return referenceData?.incentive;
      case "TRIBUTE":
        return referenceData?.tribute;
      case "SUPPLIER_TRIBUTE":
        return (
          referenceData?.supplier_tribute || {
            typeTax: "PERCENTAGE",
            value: 0,
            currencyValue: 0,
            valueBRL: 0,
          }
        );
      case "DOUBLE_CHARGED":
        return referenceData?.doubleCharged;
      default:
        return {
          typeTax: "",
          value: 0,
          currencyValue: 0,
          valueBRL: 0,
        } as DefaultTax;
    }
  }

  const filteredOptions = typeTaxOptions.filter((el: any) => {
    if (itemType !== "ACCOMMODATIONS" && el.value === "TOURISM_TAX")
      return false;
    if (["BRL", ""].includes(exchange?.currency) && el.value === "IOF")
      return false;
    if (!["BRL"].includes(exchange?.currency) && el.value === "ISS")
      return false;
    if (el.value === "DOUBLE_CHARGED" && !canEditBiTributed) return false;
    if (el.value === "TARIFF" || el.value === "EXTRAS") return false;
    if (el.value === "OVER") return false;
    return true;
  });

  const READ_ONLY_OPTIONS = [
    {
      label: "Total de Tarifa",
      readonly: true,
      value: {
        typeTax: "FIX_VALUE",
        value: totals.tariffTotals.totalInBRL,
        currencyValue: totals.tariffTotals.totalInCurrency,
        valueBRL: totals.tariffTotals.totalInBRL,
      },
    },
  ];

  const augmentedOptions = [...READ_ONLY_OPTIONS, ...filteredOptions];

  return (
    <Box>
      {chunkArray(augmentedOptions, 5).map(
        (group: any[], groupIndex: number) => (
          <Box
            key={groupIndex}
            sx={{
              display: "flex",
              flexDirection: "row",
              mb: 2,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {group.map((el: any, index: number) => {
              if (el.readonly) {
                return (
                  <Box
                    key={index}
                    sx={{ flex: 1, mr: index !== group.length - 1 ? 2 : 0 }}
                  >
                    <TaxWithConvertedValue
                      type="TARIFF"
                      title={el.label}
                      previousState={el.value}
                      calculusOptions={valueTypeOptionsDefault}
                      triggerCalculus={() => {}}
                      disableChanges={true}
                      selectedCurrencyCode={exchange.currency}
                      comparisonCurrencyCode={compareExchange.currency}
                      comparisonState={definePreviousValue(
                        "SERVICE",
                        compareTaxData
                      )}
                      hasTotal={false}
                      foreignDecimalScale={2}
                    />
                  </Box>
                );
              }

              const key: TaxKey =
                mapTypeTaxToTaxKey(typeTax[el.value as keyof typeof typeTax]) ||
                "localTax";

              const calcOption =
                el.value === "TARIFF"
                  ? valueCortesyOptions
                  : el.value === "NFMM"
                  ? nfMMOptions
                  : el.value === "ISS"
                  ? valueIssTypeOptionsDefault
                  : el.value === "SUPPLIER_TRIBUTE"
                  ? percentageOnlyOptions
                  : valueTypeOptionsDefault;

              return (
                <Box
                  key={index}
                  sx={{ flex: 1, mr: index !== group.length - 1 ? 2 : 0 }}
                >
                  <TaxWithConvertedValue
                    type={el.value}
                    title={el.label}
                    previousState={definePreviousValue(el.value, taxData)}
                    calculusOptions={calcOption}
                    triggerCalculus={handleCalcValues}
                    disableChanges={false}
                    selectedCurrencyCode={exchange.currency}
                    comparisonCurrencyCode={compareExchange.currency}
                    comparisonState={definePreviousValue(
                      el.value,
                      compareTaxData
                    )}
                    hasTotal={false}
                  />
                </Box>
              );
            })}

            {Array.from({ length: 5 - group.length }).map((_, fillerIndex) => (
              <Box
                key={`filler-${fillerIndex}`}
                sx={{
                  flex: 1,
                  mr: fillerIndex !== 5 - group.length - 1 ? 2 : 0,
                }}
              />
            ))}
          </Box>
        )
      )}
    </Box>
  );
};

export default BaseMonetaryData;
